import { V1Service } from '@zelos/codegen/apis';
import { regex } from '@zelos/shared/constants/regex';
import { setReactHookFormErrorByErrors, toTypedApiError } from '@zelos/shared/lib/typed-api-error';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { InputAboveDiv, InputWrapDiv, InputWrapSpan } from '../SignInput';
import { ResetPasswordForm, ContentFirstDiv, ContentSecondDiv, BottomBtn } from './ResetPasswordComponents';
import ImageButton from '@/components/comm/ImageButton';
import Input from '@/components/comm/Input';
import RequiredAsterisk from '@/components/comm/RequiredAsterisk';
import { createErrorToast, showToast } from '@/lib/toast';
import icEyeSlash from '@/public/images/ic-eye-slash.svg';
import icEye from '@/public/images/ic-eye.svg';

type ResetPasswordConfirmType = {
  password: string,
  passwordConfirm: string,
};

const ResetPasswordConfirm = () => {
  const router = useRouter();
  const { register, handleSubmit, watch, setError, formState: { errors } } = useForm<ResetPasswordConfirmType>();
  const watchPassword = watch('password', '');

  const [showPassword, setShowPassword] = useState(false);

  const token = useRef('');
  const signToken = useRef('');

  const validateToken = useCallback(async () => {
    try {
      await V1Service.v1UsersPasswordsValidateTokenCreate({
        requestBody: {
          signingToken: signToken.current,
          token: token.current,
        },
      });
    } catch (error) {
      const errorBody = toTypedApiError(error);
      if (errorBody) {
        if (errorBody.status === 404) {
          showToast({ text: '만료된 링크입니다.' });
        } else {
          createErrorToast(errorBody);
        }
        router.replace('/');
      } else {
        throw error;
      }
    }
  }, [router]);

  const resetPasswordSubmit = async ({ password }: ResetPasswordConfirmType) => {
    try {
      const result = await V1Service.v1UsersPasswordsValidateTokenCreate({
        requestBody: {
          signingToken: signToken.current,
          token: token.current,
        },
      });
      await V1Service.v1UsersPasswordsResetPasswordCreate({
        requestBody: {
          ...result,
          password,
        },
      });
      showToast({ text: '비밀번호가 변경되었습니다' });
      router.replace('/');
    } catch (error) {
      const errorBody = toTypedApiError(error);
      if (errorBody) {
        setReactHookFormErrorByErrors(setError, errorBody.errors);
        createErrorToast(errorBody, 'detail');
      } else {
        throw error;
      }
    }
  };

  useEffect(() => {
    const { signing_token, token: _token } = router.query;
    if (typeof signing_token === 'string' && typeof _token === 'string') {
      signToken.current = signing_token;
      token.current = _token;
      validateToken();
    } else {
      showToast({ text: '올바르지 않은 접근입니다' });
      router.replace('/');
    }
  }, [router, validateToken]);

  return (
    <ResetPasswordForm noValidate onSubmit={handleSubmit(resetPasswordSubmit)}>
      <ContentFirstDiv>비밀번호 재설정</ContentFirstDiv>
      <ContentSecondDiv>새롭게 설정할 비밀번호를 입력해 주세요.</ContentSecondDiv>
      <InputWrapDiv>
        <InputAboveDiv>
          <InputWrapSpan>
            <span>새로운 비밀번호</span>
            <RequiredAsterisk />
          </InputWrapSpan>
        </InputAboveDiv>
        <Input
          type={showPassword ? 'text' : 'password'}
          placeholder="(문자, 숫자, 특수문자 포함 최소 8자)"
          error={errors.password}
          {...register('password', {
            required: '비밀번호를 입력해 주세요',
            pattern: {
              value: regex.password,
              message: '문자, 숫자, 특수문자 포함 최소 8자를 입력해 주세요.',
            },
          })}
          end={(
            <ImageButton
              onClick={() => setShowPassword(!showPassword)}
              src={showPassword ? icEye : icEyeSlash}
              alt="show password"
              width={24}
              height={17}
              style={{
                objectFit: 'contain',
              }}
            />
          )}
        />
      </InputWrapDiv>
      <InputWrapDiv>
        <InputAboveDiv>
          <InputWrapSpan>
            <span>비밀번호 재입력</span>
            <RequiredAsterisk />
          </InputWrapSpan>
        </InputAboveDiv>
        <Input
          type="password"
          placeholder="비밀번호를 확인해 주세요"
          error={errors.password}
          {...register('passwordConfirm', {
            required: '비밀번호를 확인해 주세요',
            pattern: {
              value: regex.password,
              message: '문자, 숫자, 특수문자 포함 최소 8자를 입력해 주세요.',
            },
            validate: {
              positive: (value) => value === watchPassword || '비밀번호가 일치하지 않습니다.',
            },
          })}
        />
      </InputWrapDiv>
      <BottomBtn type="submit">저장하기</BottomBtn>
    </ResetPasswordForm>
  );
};

export default ResetPasswordConfirm;
